import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SummaryFigures } from "../types";
import { fmt } from "./format";
import { equationText } from "./reportSections";

// Drawing pieces shared by the Financial Report PDF and the Monthly Brief
// PDF, so the summary boxes and income / expense tables look identical in
// both. They draw values exactly as given — no arithmetic.

export type RGB = [number, number, number];

export const C = {
  text: [33, 33, 33] as RGB,
  muted: [120, 120, 120] as RGB,
  line: [224, 224, 224] as RGB,
  slate: [55, 71, 79] as RGB,
  slateBg: [236, 239, 241] as RGB,
  green: [46, 125, 50] as RGB,
  greenBg: [232, 245, 233] as RGB,
  red: [198, 40, 40] as RGB,
  redBg: [253, 236, 234] as RGB,
  amber: [230, 145, 0] as RGB,
  amberBg: [255, 248, 225] as RGB,
  headBg: [245, 246, 248] as RGB,
};

export const MARGIN = 14;
export const FOOTER_SPACE = 14;
const TOP = 16;

// jsPDF's built-in fonts only cover WinAnsi — keep text ASCII (no emoji/em dashes).
// (toLocaleString also emits narrow no-break spaces, e.g. "3:30 p.m.")
export const ascii = (s: string) => s.replace(/[—–]/g, "-").replace(/[  ]/g, " ");

/** A jsPDF document plus a vertical cursor, with page-aware helpers. */
export class PdfWriter {
  readonly doc = new jsPDF("p", "mm", "a4");
  readonly pageW = this.doc.internal.pageSize.getWidth();
  readonly pageH = this.doc.internal.pageSize.getHeight();
  readonly contentW = this.pageW - MARGIN * 2;
  y = TOP;

  readonly tableBase = {
    margin: { left: MARGIN, right: MARGIN, bottom: FOOTER_SPACE },
    styles: { fontSize: 8.5, cellPadding: 1.6, textColor: C.text, lineColor: C.line, lineWidth: 0.1 },
    columnStyles: { 1: { halign: "right" as const, cellWidth: 40 } },
    theme: "grid" as const,
  };

  newPage() {
    this.doc.addPage();
    this.y = TOP;
  }

  ensureSpace(needed: number) {
    if (this.y + needed > this.pageH - FOOTER_SPACE) this.newPage();
  }

  lastTableEnd() {
    return (this.doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  }

  /** Centered line of text. */
  centered(text: string, size: number, color: RGB, bold = false, advance = 5) {
    this.doc.setFont("helvetica", bold ? "bold" : "normal");
    this.doc.setFontSize(size);
    this.doc.setTextColor(...color);
    this.doc.text(ascii(text), this.pageW / 2, this.y, { align: "center" });
    this.y += advance;
  }

  sectionTitle(title: string, minFollowing = 24, size = 10.5) {
    this.ensureSpace(8 + minFollowing);
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(size);
    this.doc.setTextColor(...C.text);
    this.doc.text(ascii(title), MARGIN, this.y);
    this.y += 2;
  }

  /** The 4 coloured boxes (Carry Forward / Income / Expenses / Balance) and the equation under them. */
  summaryBoxes(f: SummaryFigures) {
    const { doc } = this;
    const positive = f.balanceOnHand >= 0;
    const boxes: { label: string; value: string; fg: RGB; bg: RGB }[] = [
      { label: "Carry Forward", value: fmt(f.carryForward), fg: C.slate, bg: C.slateBg },
      { label: "Total Income", value: `+ ${fmt(f.incomeTotal)}`, fg: C.green, bg: C.greenBg },
      { label: "Total Expenses", value: `- ${fmt(f.expenseTotal)}`, fg: C.red, bg: C.redBg },
      { label: "Balance on Hand", value: fmt(f.balanceOnHand), fg: positive ? C.green : C.red, bg: positive ? C.greenBg : C.redBg },
    ];
    const gap = 3;
    const boxW = (this.contentW - gap * 3) / 4;
    const boxH = 15;
    this.ensureSpace(boxH + 12);
    boxes.forEach((b, i) => {
      const x = MARGIN + i * (boxW + gap);
      doc.setFillColor(...b.bg);
      doc.setDrawColor(...b.fg);
      doc.setLineWidth(0.2);
      doc.roundedRect(x, this.y, boxW, boxH, 1.5, 1.5, "FD");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...C.muted);
      doc.text(b.label, x + boxW / 2, this.y + 5, { align: "center" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11.5);
      doc.setTextColor(...b.fg);
      doc.text(b.value, x + boxW / 2, this.y + 11.2, { align: "center" });
    });
    this.y += boxH + 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...C.muted);
    doc.text(equationText(f), MARGIN, this.y);
    this.y += 7;
  }

  /** Detailed Income (with a separate Partner Deposits group) then Detailed Expenses. */
  incomeAndExpenseTables(f: SummaryFigures) {
    this.sectionTitle("Detailed Income");
    const incomeBody: (string | { content: string; colSpan?: number; styles?: object })[][] = f.incomeBreakdown.map((r) => [r.name, fmt(r.amount)]);
    if (f.depositBreakdown.length) {
      incomeBody.push([{ content: "Partner Deposits", colSpan: 2, styles: { fontStyle: "bold", textColor: C.muted, fontSize: 7.5 } }]);
      f.depositBreakdown.forEach((r) => incomeBody.push([r.name, fmt(r.amount)]));
    }
    if (!incomeBody.length) incomeBody.push([{ content: "No income in this period.", styles: { textColor: C.muted } }, ""]);
    autoTable(this.doc, {
      ...this.tableBase,
      startY: this.y,
      head: [["Income Source", { content: "Amount", styles: { halign: "right" } }]],
      body: incomeBody,
      foot: [["Total Income", { content: fmt(f.incomeTotal), styles: { halign: "right" } }]],
      headStyles: { fillColor: C.greenBg, textColor: C.text, fontStyle: "bold" },
      footStyles: { fillColor: C.greenBg, textColor: C.green, fontStyle: "bold" },
    });
    this.y = this.lastTableEnd() + 7;

    this.sectionTitle("Detailed Expenses");
    autoTable(this.doc, {
      ...this.tableBase,
      startY: this.y,
      head: [["Expense Category", { content: "Amount", styles: { halign: "right" } }]],
      body: f.expenseBreakdown.length
        ? f.expenseBreakdown.map((r) => [r.name, fmt(r.amount)])
        : [[{ content: "No expenses in this period.", styles: { textColor: C.muted } }, ""]],
      foot: [["Total Expenses", { content: fmt(f.expenseTotal), styles: { halign: "right" } }]],
      headStyles: { fillColor: C.redBg, textColor: C.text, fontStyle: "bold" },
      footStyles: { fillColor: C.redBg, textColor: C.red, fontStyle: "bold" },
    });
    this.y = this.lastTableEnd() + 7;
  }

  /** "<left>" on the left, "Generated <time>   Page X of Y" on the right, on every page. Call last. */
  footers(left: string, generatedAt: Date) {
    const { doc } = this;
    const pages = doc.getNumberOfPages();
    const stamp = ascii(generatedAt.toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" }));
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      const fy = this.pageH - 8;
      doc.setDrawColor(...C.line);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, fy - 4, this.pageW - MARGIN, fy - 4);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...C.muted);
      const right = `Generated ${stamp}   Page ${p} of ${pages}`;
      doc.text(right, this.pageW - MARGIN, fy, { align: "right" });
      // Keep the left label clear of the right-hand text.
      const room = this.contentW - doc.getTextWidth(right) - 6;
      let label = ascii(left);
      while (label.length > 4 && doc.getTextWidth(label) > room) label = `${label.slice(0, -4)}...`;
      doc.text(label, MARGIN, fy);
    }
  }
}
